import React, { useCallback, useEffect, useRef } from 'react';

import type { EChartsOption } from 'echarts';
import { BarChart, LineChart } from 'echarts/charts';
import { AriaComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import { init, use as registerECharts } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import { Box, Paper, Typography } from '@mui/material';

import { inventoryBand } from './prototypeStyle';

registerECharts([
  BarChart,
  LineChart,
  AriaComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type Props = {
  option: EChartsOption;
  title: string;
  source: string;
  range: string;
  onSelect?: (value: { product: string; date?: string }) => void;
  height?: number;
};

/** Native ECharts runtime; only the pure chart-option builder is shared with standalone. */
export default function InventoryChart({ option, title, source, range, onSelect, height }: Props) {
  const element = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof init> | undefined>(undefined);
  const pendingOption = useRef<EChartsOption | undefined>(undefined);
  const syncChart = useCallback(() => {
    const node = element.current;
    const chart = instance.current;
    // A hidden or unmounting map has a singular transform at zero size.
    // Keep its pending option until ResizeObserver reports a visible surface.
    if (!node?.clientWidth || !node.clientHeight || !chart || chart.isDisposed()) return;
    chart.resize();
    if (pendingOption.current) {
      chart.setOption(pendingOption.current, { notMerge: true });
      pendingOption.current = undefined;
    }
  }, []);
  const select = useRef(onSelect);
  useEffect(() => {
    select.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    if (!element.current) {
      return undefined;
    }
    const chart = init(element.current, undefined, { renderer: 'canvas' });
    instance.current = chart;
    chart.on('click', (event: { seriesName?: string; name?: string }) => {
      if (event.seriesName) {
        select.current?.({ product: event.seriesName, date: event.name });
      }
    });
    const observer = new ResizeObserver(syncChart);
    observer.observe(element.current);
    return () => {
      observer.disconnect();
      chart.dispose();
      instance.current = undefined;
    };
  }, [syncChart]);
  useEffect(() => {
    pendingOption.current = option;
    syncChart();
  }, [option, syncChart]);
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', minWidth: 0 }}>
      <Typography component="h2" variant="h6" sx={inventoryBand}>
        {title}
      </Typography>
      <Box
        ref={element}
        role="img"
        aria-label={`${title}. ${range}. ${onSelect ? 'Select a series to filter.' : ''}`}
        sx={{
          width: '100%',
          height: height
            ? { xs: Math.min(height, 320), md: Math.min(height, 480) }
            : { xs: 280, sm: 'clamp(300px, 28vw, 480px)' },
          minWidth: 0,
        }}
      />
      <Typography
        variant="caption"
        title={source}
        color="text.secondary"
        sx={{ display: 'block', px: 0.75, pb: 0.5, fontSize: 10, overflowWrap: 'anywhere' }}
      >
        {range} · Availability dates · Missing values remain gaps.
      </Typography>
    </Paper>
  );
}
